/**
 * Find and enrol on courses.
 *
 * Provides clean segmented subject filtering, unboxed metadata, and clear
 * enrollment actions for learners.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiDelete, apiGet, apiPost } from "../../api/client";
import type { CurriculumSummary } from "../../types";

type AvailableCourse = {
  id: string;
  title: string;
  subject: string;
  grade_label: string;
  lesson_count: number;
  publisher: string;
  teachers: { id: string; name: string }[];
};

const SUBJECT_TABS = [
  { id: "all", label: "All Subjects" },
  { id: "math", label: "Mathematics" },
  { id: "math-lit", label: "Maths Literacy" },
  { id: "physical-sciences", label: "Physical Sciences" },
  { id: "accounting", label: "Accounting" },
  { id: "english", label: "English FAL / HL" },
  { id: "tech", label: "Programming / IT" },
];

export function BrowsePage() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [teacherFor, setTeacherFor] = useState<Record<string, string>>({});
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const {
    data: available,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["available-subjects"],
    queryFn: () => apiGet<AvailableCourse[]>("/api/curricula/available"),
  });

  const { data: enrolled } = useQuery({
    queryKey: ["curricula"],
    queryFn: () => apiGet<CurriculumSummary[]>("/api/curricula"),
  });

  const enrolledIds = new Set((enrolled ?? []).map((c) => c.id));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["curricula"] });
    queryClient.invalidateQueries({ queryKey: ["available-subjects"] });
  };

  const enrol = useMutation({
    mutationFn: ({ id, instructor }: { id: string; instructor?: string }) =>
      apiPost("/api/learners/me/subjects", {
        curriculum_id: id,
        instructor_id: instructor ?? null,
      }),
    onSuccess: () => {
      setNotice("Subject added to your curriculum portfolio.");
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  const drop = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/learners/me/subjects/${id}`),
    onSuccess: () => {
      setNotice("Subject removed. Your completed dialogue history is safely preserved.");
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  const filteredCourses = useMemo(() => {
    if (!available) return [];
    let list = available;

    if (activeTab !== "all") {
      list = list.filter((c) => {
        const s = (c.subject + " " + c.id).toLowerCase();
        if (activeTab === "math") return s.includes("mathematics") && !s.includes("literacy");
        if (activeTab === "math-lit") return s.includes("literacy");
        if (activeTab === "physical-sciences") return s.includes("physical-sciences") || s.includes("physical sciences");
        if (activeTab === "accounting") return s.includes("accounting");
        if (activeTab === "english") return s.includes("english");
        if (activeTab === "tech") return s.includes("python") || s.includes("programming") || s.includes("network") || s.includes("project-management");
        return true;
      });
    }

    const needle = query.trim().toLowerCase();
    if (needle) {
      list = list.filter((c) =>
        [c.title, c.subject, c.grade_label, c.publisher]
          .concat(c.teachers.map((t) => t.name))
          .some((f) => f?.toLowerCase().includes(needle))
      );
    }

    return list;
  }, [available, activeTab, query]);

  if (isLoading) return <p className="text-muted">Loading curriculum catalogue...</p>;

  return (
    <div className="browse-page">
      <header className="browse-header">
        <h1>Course Catalogue</h1>
        <p className="browse-subtitle">
          South African CAPS curriculum modules and specialized tracks. Enrol in a subject
          to access guided Socratic dialogues and examination prep.
        </p>
      </header>

      {notice && (
        <div className="notice" role="status">
          <span>{notice}</span>
          <button type="button" className="notice-dismiss" onClick={() => setNotice("")}>
            ✕
          </button>
        </div>
      )}

      {/* Segmented Filter Bar */}
      <div className="browse-controls-row">
        <div className="segmented-control" role="tablist" aria-label="Filter subjects">
          {SUBJECT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`segmented-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="browse-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by topic, grade, or teacher..."
            aria-label="Search courses"
          />
        </div>
      </div>

      {isError ? (
        <div className="card text-center">
          <p className="text-muted">Could not load the catalogue right now.</p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => refetch()}
            style={{ marginTop: 12 }}
          >
            Retry Connection
          </button>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="card text-center browse-empty-state">
          <h3>No matching subjects found</h3>
          <p className="text-muted">
            Try adjusting your search query or selecting a different subject tab above.
          </p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setQuery("");
              setActiveTab("all");
            }}
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="browse-grid">
          {filteredCourses.map((c) => {
            const already = enrolledIds.has(c.id);
            return (
              <div key={c.id} className={`card browse-card ${already ? "is-enrolled" : ""}`}>
                <div className="browse-card-body">
                  <div className="browse-card-top">
                    <span className="browse-grade-tag">{c.grade_label || "Curriculum"}</span>
                    {already && <span className="browse-enrolled-indicator">Enrolled</span>}
                  </div>

                  <h2 className="browse-card-title">{c.title}</h2>

                  {/* Clean unboxed metadata with subtle dots */}
                  <div className="browse-meta-line">
                    <span>{c.subject}</span>
                    <span aria-hidden="true">·</span>
                    <span>{c.lesson_count} {c.lesson_count === 1 ? "Lesson" : "Lessons"}</span>
                    <span aria-hidden="true">·</span>
                    <span>{c.publisher}</span>
                  </div>

                  {c.teachers.length > 0 && (
                    <div className="browse-teachers-line">
                      Instructors: {c.teachers.map((t) => t.name).join(", ")}
                    </div>
                  )}

                  {!already && c.teachers.length > 1 && (
                    <div className="browse-teacher-select">
                      <label htmlFor={`teacher-${c.id}`} className="field-label">
                        Select cohort instructor:
                      </label>
                      <select
                        id={`teacher-${c.id}`}
                        value={teacherFor[c.id] ?? ""}
                        onChange={(e) =>
                          setTeacherFor((t) => ({ ...t, [c.id]: e.target.value }))
                        }
                      >
                        <option value="">Default Cohort</option>
                        {c.teachers.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="browse-card-footer">
                  {already ? (
                    <div className="browse-actions-enrolled">
                      <Link to={`/learn/${c.id}`} className="btn btn-primary btn-sm">
                        Open Roadmap →
                      </Link>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={drop.isPending}
                        onClick={() => drop.mutate(c.id)}
                      >
                        Drop
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm btn-enrol"
                      disabled={enrol.isPending}
                      onClick={() =>
                        enrol.mutate({ id: c.id, instructor: teacherFor[c.id] })
                      }
                    >
                      + Enrol in Subject
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
